/**
 * @file useVerifyEmail.js
 * Custom hook for managing email verification in the registration process.
 * It handles form state, validation, and submission logic for verifying email and OTP.
 * It also manages the timer for resending OTP and transitions between slides.
 * This hook is used in the registration flow to ensure that the user verifies their email before proceeding.
 */
import React from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useAuthNavigation from '../../useAuthNavigation';
import {
    checkEmailExists,
    sentOtpToEmail,
    registerWithVerify,
    resendOtp,
    setEmail
} from '../../../../app/store/slices/auth/authSlice';
import { EMAIL_REGEX } from '../../../../utils/validations';
import { trimAndCleanSpaces } from '../../../../utils/trimAndCleanSpaces';

export default function useVerifyEmail() {
    const dispatch = useDispatch();
    const [activeSlide, setActiveSlide] = React.useState('slide1');
    const [seconds, setSeconds] = React.useState(0);
    const [resendLoading, setResendLoading] = React.useState(false);
    const { showCompleteProfileForm } = useAuthNavigation();

    // to run timer in resend otp email
    React.useEffect(() => {
        const timer = setInterval(() => {
            if (seconds > 0) {
                setSeconds(seconds - 1);
            }

            if (seconds === 0) {
                clearInterval(timer);
            }
        }, 1000);

        // clear the Interval function from browser
        return () => {
            clearInterval(timer);
        };
    }, [seconds]);

    const formik = useFormik({
        initialValues: {
            email: '',
            otp: ''
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email('Invalid email address')
                .required('Email is required')
                .matches(EMAIL_REGEX, 'Invalid email format'),
            otp: Yup.string().required('OTP is required')
        }),
        onSubmit: () => {}
    });

    async function verifyEmail() {
        // clean and trim email input
        const cleanEmail = trimAndCleanSpaces(formik.values.email).toLowerCase();

        try {
            // check if email exists
            const emailCheckResult = await dispatch(checkEmailExists(cleanEmail));

            // handle errors from email check
            if (emailCheckResult?.error?.message === 'Rejected') {
                formik.setFieldError('email', 'Something went wrong! Please try again.');
                formik.setSubmitting(false);
                formik.setFieldTouched('email', true, false);
                return;
            }

            // if email already exists, set error and return
            if (emailCheckResult?.payload?.exist) {
                formik.setFieldError(
                    'email',
                    'Email already in use. Please use another email or sign in.'
                );
                return;
            }

            // send OTP to the email if email does not exist
            const sentOtpResult = await dispatch(sentOtpToEmail(cleanEmail));

            // handle errors from sending OTP
            if (sentOtpResult?.error?.message === 'Rejected') {
                formik.setFieldError('email', sentOtpResult.payload);
                formik.setSubmitting(false);
                formik.setFieldTouched('email', true, false);
                return;
            }

            // if OTP is sent successfully, set the active slide to slide2, start the timer, and store email in state
            if (sentOtpResult?.payload?.status) {
                setActiveSlide('slide2');
                setSeconds(60);
                dispatch(setEmail(cleanEmail));
                formik.setSubmitting(false);
                return;
            }
        } catch (error) {
            console.log('Error verifying email:', error);
            window.localStorage.removeItem('registerEmail');
            formik.setFieldError('email', 'Something went wrong! Please try again.');
        }
    }

    async function resendOtpEmail() {
        try {
            // set resend loading state and clean the email input
            setResendLoading(true);
            const cleanEmail = trimAndCleanSpaces(formik.values.email).toLowerCase();

            // dispatch the resendOtp action with the cleaned email
            const resendOtpResult = await dispatch(resendOtp(cleanEmail));

            // if the status is true, reset the seconds
            if (resendOtpResult?.payload?.status) {
                setSeconds(60);
                formik.setSubmitting(false);
                return;
            }

            // if the status is false, set error message
            formik.setFieldError('otp', resendOtpResult.payload);
            formik.setSubmitting(false);
            formik.setFieldTouched('otp', true, false);
            setResendLoading(false);
        } catch (error) {
            formik.setFieldError('email', 'Failed to resend magic link. Please try again.');
            formik.setSubmitting(false);
            formik.setFieldTouched('email', true, false);
            setResendLoading(false);
        }
    }

    async function verifyWithOtp() {
        try {
            // clean the email and store otp from formik values
            const cleanEmail = trimAndCleanSpaces(formik.values.email).toLowerCase();
            const otp = formik.values.otp;

            // make api call to verify the OTP
            const response = await dispatch(registerWithVerify({ email: cleanEmail, otp }));

            // handle errors from OTP verification
            if (response?.error?.message === 'Rejected') {
                formik.setFieldError('otp', response.payload);
                formik.setSubmitting(false);
                formik.setFieldTouched('otp', true, false);
                return;
            }

            // if OTP verification is successful and the status is true, show the complete profile form
            if (response?.payload?.status) {
                showCompleteProfileForm();
                formik.resetForm();
                return;
            }
        } catch (error) {
            formik.setFieldError('otp', 'Failed to verify OTP. Please try again.');
            formik.setSubmitting(false);
            formik.setFieldTouched('otp', true, false);
        }
    }

    function getSlideClass(slideName) {
        if (activeSlide === slideName) return 'slide-active';
        if (activeSlide === 'slide2' && slideName === 'slide1') return 'slide-left';
        if (activeSlide === 'slide1' && slideName === 'slide2') return 'slide-right';
        return 'slide-hidden';
    }

    return {
        formik,
        getSlideClass,
        seconds,
        activeSlide,
        setActiveSlide,
        resendLoading,
        resendOtpEmail,
        verifyWithOtp,
        verifyEmail
    };
}
